import type { Brand } from '@integration-layer/index'

/**
 * Checks if a given website is a valid brand.
 *
 * @param {string} w - The website name to check.
 * @returns {boolean} Whether the website is a valid brand.
 * @private
 */
const isBrand = (w: string) => (brands as string[]).includes(w)

/**
 * Sorts an array of items by their associated brand/website.
 *
 * @template Item The type of items in the array.
 * @param {Item[]} array - The array of items to sort.
 * @param {(item: Item) => string} getWebsite - A function that extracts the website from an item.
 * @returns {Map<Brand, Item[]>} A Map of brands to their corresponding items.
 * @throws {Error} If the getWebsite function returns an invalid brand.
 * @example
 * const items = [{ website: 'amazon' }, { website: 'ebay' }];
 * const sortedItems = sortItemsByBrand(items, item => item.website);
 * // Returns a Map with items grouped by brand
 */
export const sortItemsByBrand = <Item>(
  array: Item[],
  getWebsite: (item: Item) => string
) => {
  const brandItems = array.reduce(
    (acc, item) => {
      const website = getWebsite(item) as Brand
      if (!isBrand(website!)) return acc
      acc[website] = acc[website] || []
      acc[website].push(item)
      return acc
    },
    {} as Record<Brand, Item[]>
  )
  const sortedItemsByBrand = new Map<Brand, Item[]>()
  brands.forEach((brand: Brand) => {
    if (brandItems[brand]) {
      sortedItemsByBrand.set(brand, brandItems[brand])
    }
  })
  return sortedItemsByBrand
}
